import React, {useState, useEffect, useMemo, useCallback} from 'react';
import JSZip from 'jszip';
import {voyageService, carService, printService} from '../services';
import {Voyage, Car} from '../types';
import {Layout} from '../components/layout';
import {SearchableSelect, SearchableMultiSelect, LoadingOverlay, Toast} from '../components';
import {useToast} from '../hooks/useToast';
import {buildBillsForPrint, carsCanCombine} from '../utils/carUtils';
import {batchProcess} from '../utils/asyncUtils';
import BillOfLading, {BillOfLadingData} from './BillOfLading';

const BillOfLadingPage: React.FC = () => {
    const [voyages, setVoyages] = useState<Voyage[]>([]);
    const [selectedVoyage, setSelectedVoyage] = useState<Voyage | null>(null);
    const [cars, setCars] = useState<Car[]>([]);
    const [selectedCars, setSelectedCars] = useState<Car[]>([]);
    const [isOriginal, setIsOriginal] = useState(false);
    const [isCombined, setIsCombined] = useState(false);
    const [includeRelated, setIncludeRelated] = useState(true);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [combining, setCombining] = useState(false);
    const {toast, showToast, hideToast} = useToast();

    useEffect(() => {
        const loadVoyages = async () => {
            try {
                const data = await voyageService.getAll({limit: 1000, is_archived: false});
                setVoyages(data as Voyage[]);
            } catch (error) {
                console.error('Error loading voyages:', error);
            }
        };
        loadVoyages();
    }, []);

    const sortedVoyages = useMemo(() => {
        return [...voyages].sort((a, b) => a.voyage_code.localeCompare(b.voyage_code));
    }, [voyages]);

    const reloadCars = useCallback(async () => {
        if (!selectedVoyage) return;
        setLoading(true);
        try {
            const response = await carService.getByVoyage(selectedVoyage.id, {limit: 10000});
            setCars(response.data as Car[]);
        } catch (error) {
            console.error('Error loading cars:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedVoyage]);

    useEffect(() => {
        if (!selectedVoyage) {
            setCars([]);
            setSelectedCars([]);
            return;
        }
        setSelectedCars([]);
        reloadCars();
    }, [selectedVoyage, reloadCars]);


    const getCarDisplayName = (car: Car) => {
        const make = car.make?.name || '';
        const model = car.model?.name || '';
        // Use manual_make_model as fallback when database make/model not available
        if (make && model) return `${make} ${model}`;
        return car.manual_make_model || `${make}${model}`.trim();
    };

    const carOptions = useMemo(() => {
        return cars.map(car => ({
            value: car.id,
            label: `${car.chassis_number} - ${getCarDisplayName(car)}`
        }));
    }, [cars]);

    const selectedCarIds = useMemo(() => {
        return selectedCars.map(car => car.id);
    }, [selectedCars]);

    const handleCarSelectionChange = (selectedIds: string[]) => {
        const selected = cars.filter(car => selectedIds.includes(car.id));
        setSelectedCars(selected);
    };

    // Re-resolve the selection against freshly-loaded cars so bl_number stays
    // current after combine/separate (selectedCars may hold stale objects).
    const selectedFreshCars = useMemo(
        () => selectedCars.map(sc => cars.find(c => c.id === sc.id) || sc),
        [selectedCars, cars]
    );

    // Combine is allowed only when the selection shares shipper/consignee/POD/booking
    // and isn't already a single combined group.
    const canCombineSelection = useMemo(() => carsCanCombine(selectedFreshCars), [selectedFreshCars]);
    const hasCombinedSelection = useMemo(
        () => selectedFreshCars.some(c => c.bl_number),
        [selectedFreshCars]
    );

    const billOfLadingDataList = useMemo((): BillOfLadingData[] => {
        if (!selectedVoyage || selectedFreshCars.length === 0) return [];
        // Auto-expands any selected unit that belongs to a combined group into one BL.
        return buildBillsForPrint(selectedFreshCars, cars, { isOriginal, includeRelated }, selectedVoyage);
    }, [selectedVoyage, selectedFreshCars, cars, isOriginal, includeRelated]);

    const handleCombine = async () => {
        const ids = selectedFreshCars.map(c => c.id);
        if (ids.length < 2) return;
        setCombining(true);
        try {
            const result = await carService.combineBl(ids);
            await reloadCars();
            showToast(result.message, 'success');
        } catch (error) {
            console.error('Error combining BL:', error);
            showToast((error as any)?.response?.data?.detail || 'Failed to combine units', 'error');
        } finally {
            setCombining(false);
        }
    };

    const handleSeparate = async () => {
        const ids = selectedFreshCars.filter(c => c.bl_number).map(c => c.id);
        if (ids.length === 0) return;
        setCombining(true);
        try {
            const result = await carService.separateBl(ids);
            await reloadCars();
            showToast(result.message, 'success');
        } catch (error) {
            console.error('Error separating BL:', error);
            showToast((error as any)?.response?.data?.detail || 'Failed to separate units', 'error');
        } finally {
            setCombining(false);
        }
    };

    const generatePDF = async () => {
        if (billOfLadingDataList.length === 0) return;

        setGenerating(true);
        const type = isOriginal ? 'Original' : 'Copy';

        try {
            if (isCombined && billOfLadingDataList.length > 1) {
                // Combined BL - single PDF with multiple pages
                const filename = `Combined Bill of Lading ${selectedVoyage?.voyage_code} ${type}.pdf`;
                await printService.generateCombinedBillOfLading({bills: billOfLadingDataList}, filename);
            } else if (billOfLadingDataList.length === 1) {
                // Single car - download directly
                const data = billOfLadingDataList[0];
                const filename = `Bill of Lading ${data.blNumber} ${type}.pdf`;
                await printService.generateBillOfLading(data, filename);
            } else {
                // Multiple cars - generate ZIP with parallel batching
                const zip = new JSZip();

                // Process PDFs in parallel batches of 3
                const results = await batchProcess(
                    billOfLadingDataList,
                    async (data) => {
                        try {
                            const blob = await printService.getBillOfLadingBlob(data);
                            return {data, blob, error: null};
                        } catch (error) {
                            console.error(`Error generating PDF for ${data.blNumber}:`, error);
                            return {data, blob: null, error};
                        }
                    },
                    3
                );

                // Add successful PDFs to zip
                for (const result of results) {
                    if (result.blob) {
                        const filename = `Bill of Lading ${result.data.blNumber} ${type}.pdf`;
                        zip.file(filename, result.blob);
                    }
                }

                // Generate and download ZIP
                const zipBlob = await zip.generateAsync({type: 'blob'});
                const zipFilename = `Bills of Lading ${selectedVoyage?.voyage_code} ${type}.zip`;
                const url = URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = zipFilename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setGenerating(false);
        }
    };

    // Generate all cars in voyage as a ZIP with individual PDFs + combined PDF
    const generateAllPDFs = async () => {
        if (!selectedVoyage || cars.length === 0) return;

        setGenerating(true);
        const type = isOriginal ? 'Original' : 'Copy';

        try {
            const zip = new JSZip();

            // Generate BillOfLadingData for ALL cars in voyage, expanding combined groups
            const allBillsData: BillOfLadingData[] = buildBillsForPrint(cars, cars, { isOriginal, includeRelated }, selectedVoyage);

            // Generate individual PDFs in parallel batches of 3
            const results = await batchProcess(
                allBillsData,
                async (data) => {
                    try {
                        const blob = await printService.getBillOfLadingBlob(data);
                        return {data, blob, error: null};
                    } catch (error) {
                        console.error(`Error generating PDF for ${data.blNumber}:`, error);
                        return {data, blob: null, error};
                    }
                },
                3
            );

            // Add successful PDFs to zip
            for (const result of results) {
                if (result.blob) {
                    const filename = `Bill of Lading ${result.data.blNumber} ${type}.pdf`;
                    zip.file(filename, result.blob);
                }
            }

            // Generate combined PDF
            try {
                const combinedBlob = await printService.getCombinedBillOfLadingBlob({bills: allBillsData});
                zip.file(`Combined Bills of Lading ${selectedVoyage.voyage_code} ${type}.pdf`, combinedBlob);
            } catch (error) {
                console.error('Error generating combined PDF:', error);
            }

            // Generate and download ZIP
            const zipBlob = await zip.generateAsync({type: 'blob'});
            const zipFilename = `All Bills of Lading ${selectedVoyage.voyage_code} ${type}.zip`;
            const url = URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = zipFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating all PDFs:', error);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        Bill of Lading
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Select a voyage and chassis number to generate the Bill of Lading
                    </p>

                    <div className="flex gap-4 items-center flex-wrap">
                        <div className="w-52">
                            <SearchableSelect
                                options={sortedVoyages.map(voyage => ({
                                    value: voyage.id,
                                    label: `${voyage.voyage_code} - ${voyage.vessel_description}`
                                }))}
                                value={selectedVoyage?.id || ''}
                                onChange={(value) => {
                                    const voyage = sortedVoyages.find(v => v.id === value);
                                    setSelectedVoyage(voyage || null);
                                }}
                                placeholder="-- Select Voyage --"
                            />
                        </div>

                        <div className="w-64">
                            <SearchableMultiSelect
                                options={carOptions}
                                value={selectedCarIds}
                                onChange={handleCarSelectionChange}
                                placeholder="-- Select Chassis Numbers --"
                                allLabel=""
                                disabled={!selectedVoyage || cars.length === 0}
                            />
                        </div>

                        <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="blType"
                                    checked={isOriginal}
                                    onChange={() => setIsOriginal(true)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Original</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="blType"
                                    checked={!isOriginal}
                                    onChange={() => setIsOriginal(false)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Copy</span>
                            </label>
                        </div>

                        <label
                            className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <input
                                type="checkbox"
                                checked={includeRelated}
                                onChange={(e) => setIncludeRelated(e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Include Related</span>
                        </label>

                        {selectedFreshCars.length > 1 && (
                            <button
                                onClick={handleCombine}
                                disabled={combining || !canCombineSelection}
                                title={canCombineSelection ? 'Combine selected units onto one BL (shared B/L No.)' : 'Units must share shipper, consignee, POD and booking to combine'}
                                className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-medium rounded-lg transition-colors"
                            >
                                {combining ? 'Working...' : `Combine onto one BL (${selectedFreshCars.length})`}
                            </button>
                        )}

                        {hasCombinedSelection && (
                            <button
                                onClick={handleSeparate}
                                disabled={combining}
                                className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white font-medium rounded-lg transition-colors"
                            >
                                {combining ? 'Working...' : 'Separate from BL'}
                            </button>
                        )}

                        {selectedCars.length > 1 && (
                            <label
                                className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <input
                                    type="checkbox"
                                    checked={isCombined}
                                    onChange={(e) => setIsCombined(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Combined BL (one PDF, multiple pages)</span>
                            </label>
                        )}

                        {selectedVoyage && selectedCars.length > 0 && (
                            <button
                                onClick={generatePDF}
                                disabled={generating}
                                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
                            >
                                {generating
                                    ? 'Generating...'
                                    : isCombined && selectedCars.length > 1
                                        ? `Download Combined BL (${selectedCars.length} pages)`
                                        : billOfLadingDataList.length > 1
                                            ? `Download ${billOfLadingDataList.length} PDFs (ZIP)`
                                            : 'Save as PDF'}
                            </button>
                        )}

                        {selectedVoyage && cars.length > 0 && (
                            <button
                                onClick={generateAllPDFs}
                                disabled={generating}
                                className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-colors"
                            >
                                {generating ? 'Generating...' : `Download All (${cars.length} cars + Combined)`}
                            </button>
                        )}
                    </div>
                </div>

                {loading && (
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                        <p className="text-gray-500 dark:text-gray-400">Loading cars...</p>
                    </div>
                )}

                {billOfLadingDataList.length > 0 && !loading && (
                    <div className="space-y-8">
                        {billOfLadingDataList.map((data, index) => (
                            <div key={index}
                                 className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                <div
                                    className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">
                    Preview {billOfLadingDataList.length > 1 ? `(${index + 1} of ${billOfLadingDataList.length})` : ''}
                  </span>
                                    <span className="text-sm text-gray-500">{data.blNumber}</span>
                                </div>
                                <div className="p-4">
                                    <BillOfLading {...data} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!selectedVoyage && !loading && (
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                        <div className="text-gray-400 dark:text-gray-500">
                            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            <p>Select a voyage and chassis number to preview the Bill of Lading</p>
                        </div>
                    </div>
                )}
            </div>
            <LoadingOverlay
                isVisible={generating}
                message="Generating PDFs..."
                subMessage="Please wait while we prepare your files"
            />
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={hideToast}
                />
            )}
        </Layout>
    );
};

export default BillOfLadingPage;

